import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { styled } from '@mui/material'
import { colorPalette } from '../../../../style/colorPalette'
import { intl } from '../../../../../i18n'

// 表示〜自動消滅の時間。keyframes(savePillInOut) と一致させる。
const PILL_DURATION_MS = 2200
const ANCHOR_GAP_PX = 8

type ShowSavePillOptions = {
  key: string
  anchorEl?: HTMLElement | null
  rect?: DOMRect
}

type SaveIndicatorContextValue = {
  showSavePill: (options: ShowSavePillOptions) => void
}

const SaveIndicatorContext = createContext<SaveIndicatorContextValue | null>(
  null
)

type ActivePill = {
  x: number
  y: number
  nonce: number
}

type SaveIndicatorProviderProps = {
  children: ReactNode
}

export const SaveIndicatorProvider = ({
  children,
}: SaveIndicatorProviderProps) => {
  const [pills, setPills] = useState<Record<string, ActivePill>>({})
  const [liveNonce, setLiveNonce] = useState(0)
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  )

  const showSavePill = useCallback((options: ShowSavePillOptions) => {
    const { key, anchorEl, rect } = options
    setLiveNonce(n => n + 1)
    const targetRect = anchorEl?.getBoundingClientRect() ?? rect
    if (!targetRect) return

    const x = targetRect.right - ANCHOR_GAP_PX
    const y = targetRect.top + targetRect.height / 2
    setPills(prev => ({
      ...prev,
      [key]: { x, y, nonce: (prev[key]?.nonce ?? 0) + 1 },
    }))

    const timers = timersRef.current
    const existing = timers.get(key)
    if (existing) clearTimeout(existing)
    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key)
        setPills(prev => {
          const next = { ...prev }
          delete next[key]
          return next
        })
      }, PILL_DURATION_MS)
    )
  }, [])

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach(timer => clearTimeout(timer))
      timers.clear()
    }
  }, [])

  const value = useMemo(() => ({ showSavePill }), [showSavePill])
  const savedLabel = intl.formatMessage({ id: 'versionReport.saved' })

  return (
    <SaveIndicatorContext.Provider value={value}>
      {children}
      {Object.entries(pills).map(([key, pill]) => (
        <PillAnchor
          key={`${key}-${pill.nonce}`}
          style={{ left: pill.x, top: pill.y }}
        >
          <Pill>
            <CheckRoundedIcon sx={{ fontSize: 14 }} />
            {savedLabel}
          </Pill>
        </PillAnchor>
      ))}
      <VisuallyHidden aria-live="polite">
        {/* 連続保存でも再通知させるため末尾の空白で差分を作る。 */}
        {liveNonce === 0 ? '' : `${savedLabel}${' '.repeat(liveNonce % 2)}`}
      </VisuallyHidden>
    </SaveIndicatorContext.Provider>
  )
}

export const useSaveIndicator = () => {
  const context = useContext(SaveIndicatorContext)
  if (!context) {
    throw new Error(
      'useSaveIndicator must be used within a SaveIndicatorProvider'
    )
  }
  return context
}

const PillAnchor = styled('div')({
  position: 'fixed',
  transform: 'translate(-100%, -50%)',
  pointerEvents: 'none',
  zIndex: 1300,
})

const Pill = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 10px',
  borderRadius: '999px',
  backgroundColor: colorPalette.green[1],
  border: `1px solid ${colorPalette.green[2]}`,
  color: colorPalette.green[8],
  fontSize: '12px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  animation: `savePillInOut ${PILL_DURATION_MS}ms ease forwards`,
  '@keyframes savePillInOut': {
    '0%': { opacity: 0, transform: 'translateX(8px)' },
    '12%': { opacity: 1, transform: 'translateX(0)' },
    '80%': { opacity: 1, transform: 'translateX(0)' },
    '100%': { opacity: 0, transform: 'translateX(0)' },
  },
})

const VisuallyHidden = styled('div')({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
})
